/**
 * Global Sidebar
 * Loads Shopify sections dynamically and initializes tabs inside the sidebar.
 * Triggered by elements with `.js-sidebar-trigger[data-sidebar-id]`
 */
(function () {
  const TRIGGER_CLASS = 'js-sidebar-trigger';
  const SIDEBAR_TAG = 'global-sidebar';
  const ACTIVE_CLASS = 'is-active';

  /* ------------------------------------------------------------
   * Utilities
   * ------------------------------------------------------------ */

  function getSectionUrl(sectionId) {
    const base =
      (window.Shopify?.routes?.root || window.location.pathname || '/')
        .replace(/\/$/, '') || '/';

    return `${base}?sections=${encodeURIComponent(sectionId)}`;
  }

  function executeScripts(container) {
    container.querySelectorAll('script').forEach(oldScript => {
      const script = document.createElement('script');

      if (oldScript.src) {
        script.src = oldScript.src;
        script.async = false;
        document.head.appendChild(script);
      } else {
        script.textContent = oldScript.textContent;
        document.head.appendChild(script);
        document.head.removeChild(script);
      }
    });
  }

  /* ------------------------------------------------------------
   * Tabs
   * ------------------------------------------------------------ */

  function initSidebarTabs(container) {
    const buttons = container.querySelectorAll(
      '.sidebar-tabs__tab-button[data-tab-id]'
    );
    const panels = container.querySelectorAll(
      '.sidebar-tab__content[data-tab-id]'
    );

    if (!buttons.length || !panels.length) return;

    function activateTab(tabId) {
      buttons.forEach(btn =>
        btn.classList.toggle(
          ACTIVE_CLASS,
          btn.dataset.tabId === tabId
        )
      );

      panels.forEach(panel =>
        panel.classList.toggle(
          ACTIVE_CLASS,
          panel.dataset.tabId === tabId
        )
      );
    }

    // Activate first tab by default
    activateTab(buttons[0].dataset.tabId);

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        activateTab(btn.dataset.tabId);
      });
    });
  }

  /* ------------------------------------------------------------
   * Section Fetching
   * ------------------------------------------------------------ */

  function fetchSection(sectionId) {
    return fetch(getSectionUrl(sectionId))
      .then(res => res.json())
      .then(data => data[sectionId] || Object.values(data)[0] || null);
  }

  function injectSection(sidebar, html) {
    const slot =
      sidebar.querySelector('[data-sidebar-slot]') || sidebar;

    slot.innerHTML = html;
    executeScripts(slot);
    initSidebarTabs(slot);

    sidebar.dispatchEvent(
      new CustomEvent('sidebar:content-loaded', { bubbles: true })
    );
  }

  /* ------------------------------------------------------------
   * Custom Element
   * ------------------------------------------------------------ */

  class GlobalSidebar extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      if (!this.querySelector('[data-sidebar-slot]')) {
        const slot = document.createElement('div');
        slot.className = 'sidebar__slot';
        slot.setAttribute('data-sidebar-slot', '');
        this.appendChild(slot);
      }

      this.addEventListener('click', e => {
        if (
          e.target === this ||
          e.target.classList.contains('sidebar__overlay') ||
          e.target.closest('.sidebar__close')
        ) {
          this.close();
        }
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.hasAttribute('open');
    }

    open() {
      this.setAttribute('open', '');
      document.body.classList.add('sidebar-open');
    }

    close() {
      this.removeAttribute('open');
      document.body.classList.remove('sidebar-open');
    }

    load(sectionId) {
      this.setAttribute('aria-busy', 'true');

      fetchSection(sectionId)
        .then(html => {
          this.removeAttribute('aria-busy');

          if (!html) {
            console.warn('[Sidebar] Section not found:', sectionId);
            return;
          }

          injectSection(this, html);
          this.open();
        })
        .catch(err => {
          this.removeAttribute('aria-busy');
          console.error('[Sidebar] Fetch error:', err);
        });
    }
  }

  customElements.define(SIDEBAR_TAG, GlobalSidebar);

  /* ------------------------------------------------------------
   * Trigger Handling
   * ------------------------------------------------------------ */

  document.addEventListener(
    'click',
    e => {
      const trigger = e.target.closest(`.${TRIGGER_CLASS}`);
      if (!trigger) return;

      const sectionId = trigger.dataset.sidebarId;
      if (!sectionId) return;

      const sidebar = document.querySelector(SIDEBAR_TAG);
      if (!sidebar) return;

      e.preventDefault();
      e.stopPropagation();

      sidebar.load(sectionId.trim());
    },
    true
  );
})();
